import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateRange } from 'rsuite/esm/DateRangePicker';
import {
  ReportWorkspaceListResult, PaginationReportResponse, PayloadReportWorkspaceStart,
  PayloadWorkspaceDetailsById, WorkspaceDetailsByIdResult,
  ContentType,
  GetUserDetailsPayload,
  UserDetailsData,
  GetCombineReportListPayload,
} from '../utils/types';
import { MasterData } from '../../../utils/type';
import { PaginationResponse } from '../../ScreenAddSmc/Utils/TypeSmc';

const initialState = {
  reportWorkspaceList: [] as ReportWorkspaceListResult[],
  totalEntries: 0,
  isLoading: false,
  isEmailSent: false,
  reportWorkspaceDataById: {} as WorkspaceDetailsByIdResult,
  allListContents: [] as ContentType[],
  signatureData: [] as MasterData[],
  UserDetailsData: {} as UserDetailsData,
  combineReportListData: [] as string[],
  totalCombineReport: 0 as number,
  checkedIds: [] as string[],
  selectedSignature: null as MasterData | null,
  pageNumber: 1,
  pageSize: 10,
  searchParam: '',
  sortColumn: '',
  sortDirection: 'asc',
  pageNumberCust: 1,
  pageSizeCust: 10,
  searchParamCust: '',
  dateRangeData: null as DateRange | null,
  oilDateRangeData: null as DateRange | null,
};

const reportWorkspaceSlice = createSlice({
  name: 'reportWorkspaceModule',
  initialState,
  reducers: {
    // Get All WorkSpace Data List
    getReportWorkspaceListStart(state, _action: PayloadAction<PayloadReportWorkspaceStart>) {
      return {
        ...state,
        isLoading: true,
      };
    },
    setSelectedSignature(state, action:PayloadAction<MasterData | null>) {
      return {
        ...state,
        selectedSignature: action.payload,
      };
    },
    successGetReportWorkspaceList(state, action: PayloadAction<PaginationReportResponse<ReportWorkspaceListResult>>) {
      return {
        ...state,
        isLoading: false,
        reportWorkspaceList: action?.payload.Data,
        totalEntries: action.payload.TotalRecords,
      };
    },
    failureGetReportWorkspaceList(state) {
      return {
        ...state,
        isLoading: false,
      };
    },
    // Get By Id Workspace
    getWorkSpaceByIdStart(state, _action: PayloadAction<PayloadWorkspaceDetailsById>) {
      return {
        ...state,
        isLoading: true,
      };
    },
    successGetWorkspaceById(state, action: PayloadAction<WorkspaceDetailsByIdResult>) {
      return {
        ...state,
        isLoading: false,
        reportWorkspaceDataById: action?.payload as WorkspaceDetailsByIdResult,
      };
    },
    failureGetWorkspaceById(state) {
      return {
        ...state,
        isLoading: false,
      };
    },
    setPageNumber(state, action: PayloadAction<number>) {
      return {
        ...state,
        pageNumber: action.payload,
      };
    },
    setPageSize(state, action: PayloadAction<number>) {
      return {
        ...state,
        pageSize: action.payload,
      };
    },
    setSearchParam(state, action: PayloadAction<string>) {
      return {
        ...state,
        searchParam: action.payload,
      };
    },
    setSortColumn(state, action: PayloadAction<string>) {
      return {
        ...state,
        sortColumn: action.payload,
      };
    },
    setSortDirection(state, action: PayloadAction<string>) {
      return {
        ...state,
        sortDirection: action.payload,
      };
    },
    setPageNumberCust(state, action: PayloadAction<number>) {
      return {
        ...state,
        pageNumberCust: action.payload,
      };
    },
    setPageSizeCust(state, action: PayloadAction<number>) {
      return {
        ...state,
        pageSizeCust: action.payload,
      };
    },
    setSearchParamCust(state, action: PayloadAction<string>) {
      return {
        ...state,
        searchParamCust: action.payload,
      };
    },
    setDateRangeData(state, action: PayloadAction<DateRange | null>) {
      return {
        ...state,
        dateRangeData: action.payload,
      };
    },
    setOilDateRangeData(state, action: PayloadAction<DateRange | null>) {
      return {
        ...state,
        oilDateRangeData: action.payload,
      };
    },
    resetPagination(state) {
      return {
        ...state,
        searchParam: '',
        pageSize: 10,
        pageNumber: 1,
        sortColumn: '',
        sortDirection: 'asc',
        dateRangeData: null,
        oilDateRangeData: null,
      };
    },
    resetPaginationCust(state) {
      return {
        ...state,
        searchParamCust: '',
        pageSizeCust: 10,
        pageNumberCust: 1,
      };
    },
    resetWorkspaceState(state) {
      return {
        ...state,
        reportWorkspaceList: [] as ReportWorkspaceListResult[],
        reportWorkspaceDataById: {} as WorkspaceDetailsByIdResult,
      };
    },
    resetWorkspaceByIdState(state) {
      return {
        ...state,
        reportWorkspaceDataById: {} as WorkspaceDetailsByIdResult,
      };
    },
    sendEmailStart(state, _action: PayloadAction<FormData>) {
      return {
        ...state,
        isLoading: true,
      };
    },
    // Get All List Contents Data List
    getListContentsListStart(state, _action:PayloadAction<boolean>) {
      return {
        ...state,
        isLoading: true,
      };
    },
    sendEmailSuccess(state) {
      return {
        ...state,
        isLoading: false,
        isEmailSent: true,
      };
    },
    sendEmailFailure(state) {
      return {
        ...state,
        isLoading: false,
        isEmailSent: false,
      };
    },
    clearIsEmailSent(state) {
      return {
        ...state,
        isEmailSent: false,
      };
    },
    successGetListContentsList(state, action: PayloadAction<ContentType[]>) {
      return {
        ...state,
        isLoading: false,
        allListContents: action?.payload,
      };
    },
    failureGetListContentsList(state) {
      return {
        ...state,
        isLoading: false,
      };
    },
    getSignatureStart(state) {
      return {
        ...state,
        isLoading: true,
      };
    },
    getSignatureSuccess(state, action:PayloadAction<MasterData[]>) {
      return {
        ...state,
        isLoading: false,
        signatureData: action.payload,
      };
    },
    getSignatureFailure(state) {
      return {
        ...state,
        isLoading: false,
      };
    },
    // Get Use Details Data List
    getUserDetailsListStart(state, _action: PayloadAction<GetUserDetailsPayload>) {
      return {
        ...state,
        isLoading: true,
      };
    },
    successUserDetailsList(state, action: PayloadAction<UserDetailsData>) {
      return {
        ...state,
        isLoading: false,
        UserDetailsData: action?.payload,
      };
    },
    failureGetUserDetailsList(state) {
      return {
        ...state,
        isLoading: false,
      };
    },
    // Report WorkSpace List Data
    getCombineReportDataStart(state, _action: PayloadAction<GetCombineReportListPayload>) {
      return {
        ...state,
        isLoading: true,
      };
    },

    getCombineReportDataSuccess(state, action: PayloadAction<PaginationResponse<string>>) {
      return {
        ...state,
        isLoading: false,
        combineReportListData: action.payload.Data,
        totalCombineReport: action.payload.TotalRecords,
      };
    },

    getCombineReportDataFailure(state) {
      return {
        ...state,
        isLoading: false,
      };
    },
    setCheckedIds(state, action: PayloadAction<string[]>) {
      return {
        ...state,
        checkedIds: action.payload,
      };
    },
  },
});

export const {
  getReportWorkspaceListStart, successGetReportWorkspaceList, failureGetReportWorkspaceList,
  getWorkSpaceByIdStart, successGetWorkspaceById, failureGetWorkspaceById, resetWorkspaceState,
  sendEmailStart, sendEmailSuccess, sendEmailFailure,
  resetWorkspaceByIdState, getListContentsListStart, successGetListContentsList, failureGetListContentsList,
  getSignatureStart, getSignatureSuccess, getSignatureFailure,
  getUserDetailsListStart, successUserDetailsList, failureGetUserDetailsList,
  getCombineReportDataStart, getCombineReportDataSuccess, getCombineReportDataFailure,
  setCheckedIds, setSelectedSignature, clearIsEmailSent,
  setPageNumber, setPageSize, setSearchParam, resetPagination, setDateRangeData, setOilDateRangeData,
  setPageNumberCust, setPageSizeCust, setSearchParamCust, resetPaginationCust,
  setSortColumn, setSortDirection,
} = reportWorkspaceSlice.actions;
export const reportWorkspaceReducer = reportWorkspaceSlice.reducer;

export type ReportWorkspaceActions =
  | ReturnType<typeof getReportWorkspaceListStart>
  | ReturnType<typeof successGetReportWorkspaceList>
  | ReturnType<typeof failureGetReportWorkspaceList>
  | ReturnType<typeof getWorkSpaceByIdStart>
  | ReturnType<typeof successGetWorkspaceById>
  | ReturnType<typeof failureGetWorkspaceById>
  | ReturnType<typeof resetWorkspaceState>
  | ReturnType<typeof resetWorkspaceByIdState>
  | ReturnType<typeof sendEmailStart>
  | ReturnType<typeof sendEmailSuccess>
  | ReturnType<typeof sendEmailFailure>
  | ReturnType<typeof getListContentsListStart>
  | ReturnType<typeof successGetListContentsList>
  | ReturnType<typeof failureGetListContentsList>
  | ReturnType<typeof getSignatureStart>
  | ReturnType<typeof getSignatureSuccess>
  | ReturnType<typeof getSignatureFailure>
  | ReturnType<typeof getUserDetailsListStart>
  | ReturnType<typeof successUserDetailsList>
  | ReturnType<typeof failureGetUserDetailsList>
  | ReturnType<typeof getCombineReportDataStart>
  | ReturnType<typeof getCombineReportDataSuccess>
  | ReturnType<typeof getCombineReportDataFailure>
  | ReturnType<typeof setCheckedIds>
  | ReturnType<typeof setSelectedSignature>
  | ReturnType<typeof clearIsEmailSent>
  | ReturnType<typeof setPageSize>
  | ReturnType<typeof setPageNumber>
  | ReturnType<typeof setSearchParam>
  | ReturnType<typeof resetPagination>
  | ReturnType<typeof setDateRangeData>
  | ReturnType<typeof setOilDateRangeData>
  | ReturnType<typeof setPageSizeCust>
  | ReturnType<typeof setPageNumberCust>
  | ReturnType<typeof setSearchParamCust>
  | ReturnType<typeof resetPaginationCust>
  | ReturnType<typeof setSortColumn>
  | ReturnType<typeof setSortDirection>;
